import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import './AdminProperties.css';

export default function AdminProperties({ category }) {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [currentProperty, setCurrentProperty] = useState(null);
  
  // Form fields
  const [title, setTitle] = useState('');
  const [location, setLocation] = useState('');
  const [approval, setApproval] = useState('');
  const [status, setStatus] = useState('');
  const [sizes, setSizes] = useState('');
  const [price, setPrice] = useState('');
  const [tagline, setTagline] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [isPremium, setIsPremium] = useState(false);
  
  // Specific to Plots
  const [subcategory, setSubcategory] = useState('');
  
  // Detailed Information
  const [features, setFeatures] = useState('');
  const [locations, setLocations] = useState('');

  const fetchProperties = React.useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase.from('properties').select('*');
      if (category === 'premium') {
        query = query.eq('is_premium', true);
      } else {
        query = query.eq('category', category);
      }
      const { data, error } = await query;
        
      if (error) throw error;
      setProperties(data || []);
    } catch (error) {
      console.error("Error fetching properties:", error.message);
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);


  const resetForm = () => {
    setCurrentProperty(null);
    setTitle('');
    setLocation('');
    setApproval('');
    setStatus('');
    setSizes('');
    setPrice('');
    setTagline('');
    setImageFile(null);
    setImageUrl('');
    setSubcategory('');
    setIsPremium(false);
    setFeatures('');
    setLocations('');
    setIsEditing(false);
  };

  const handleEdit = (property) => {
    setCurrentProperty(property);
    setTitle(property.title || '');
    setLocation(property.location || '');
    setApproval(property.approval || '');
    setStatus(property.status || '');
    setSizes(property.sizes || '');
    setPrice(property.price || '');
    setTagline(property.tagline || '');
    setImageUrl(property.image_url || '');
    setSubcategory(property.subcategory || '');
    setIsPremium(property.is_premium || false);
    setFeatures(property.amenities ? property.amenities.join(', ') : '');
    setLocations(property.extended_details?.locationHighlights ? property.extended_details.locationHighlights.join(', ') : '');
    setIsEditing(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this property?')) {
      try {
        const { error } = await supabase.from('properties').delete().eq('id', id);
        if (error) throw error;
        fetchProperties();
      } catch (error) {
        alert('Error deleting property: ' + error.message);
      }
    }
  };

  const handleImageUpload = async () => {
    if (!imageFile) return imageUrl;
    
    const fileExt = imageFile.name.split('.').pop();
    const fileName = `${Math.random()}.${fileExt}`;
    const filePath = `${category}/${fileName}`;

    try {
      const { error: uploadError } = await supabase.storage
        .from('website-assets')
        .upload(filePath, imageFile);
        
      if (uploadError) throw uploadError;

      const { data } = supabase.storage.from('website-assets').getPublicUrl(filePath);
      return data.publicUrl;
    } catch (error) {
      alert('Error uploading image: ' + error.message);
      return imageUrl; // Fallback to old URL
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let finalImageUrl = imageUrl;
      if (imageFile) {
        finalImageUrl = await handleImageUpload();
      }

      const actualCategory = currentProperty ? currentProperty.category : category;
      const propertyData = {
        title,
        location,
        approval,
        status,
        sizes,
        price,
        tagline,
        image_url: finalImageUrl,
        category: actualCategory,
        subcategory: actualCategory === 'plots' ? subcategory : null,
        is_premium: isPremium,
        amenities: features ? features.split(',').map(f => f.trim()).filter(f => f !== '') : [],
        extended_details: {
          ...(currentProperty?.extended_details || {}),
          locationHighlights: locations ? locations.split(',').map(l => l.trim()).filter(l => l !== '') : []
        }
      };

      if (currentProperty) {
        const { error } = await supabase
          .from('properties')
          .update(propertyData)
          .eq('id', currentProperty.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('properties')
          .insert([propertyData]);
        if (error) throw error;
      }

      resetForm();
      fetchProperties();
    } catch (error) {
      alert('Error saving property: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-properties">
      {isEditing ? (
        <div className="admin-form-container">
          <h3>{currentProperty ? 'Edit Property' : 'Add New Property'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Title</label>
              <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} required />
            </div>
            
            {category === 'plots' && (
              <div className="form-group">
                <label>Subcategory</label>
                <select value={subcategory} onChange={(e) => setSubcategory(e.target.value)} required>
                  <option value="">Select a subcategory</option>
                  <option value="open plots">Open Plots</option>
                  <option value="villa plots">Villa Plots</option>
                  <option value="premium villa plots">Premium Villa Plots</option>
                  <option value="investment plots">Investment Plots</option>
                </select>
              </div>
            )}

            <div className="form-group">
              <label>Location</label>
              <input type="text" value={location} onChange={(e) => setLocation(e.target.value)} />
            </div>

            <div className="form-group">
              <label>Approval (e.g., TG RERA Approved)</label>
              <input type="text" value={approval} onChange={(e) => setApproval(e.target.value)} />
            </div>

            <div className="form-group">
              <label>Status (e.g., Ready to Move In)</label>
              <input type="text" value={status} onChange={(e) => setStatus(e.target.value)} />
            </div>

            <div className="form-group">
              <label>Sizes (e.g., 2 BHK / 150 Sq.Yds)</label>
              <input type="text" value={sizes} onChange={(e) => setSizes(e.target.value)} />
            </div>
            
            <div className="form-group">
              <label>Price</label>
              <input type="text" value={price} onChange={(e) => setPrice(e.target.value)} />
            </div>

            <div className="form-group">
              <label>Tagline</label>
              <input type="text" value={tagline} onChange={(e) => setTagline(e.target.value)} />
            </div>

            <div className="form-group">
              <label>Amenities & Features (Comma separated)</label>
              <textarea 
                value={features} 
                onChange={(e) => setFeatures(e.target.value)} 
                placeholder="e.g. Swimming Pool, Gym, 24/7 Security"
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>Location & Connectivity (Comma separated)</label>
              <textarea 
                value={locations} 
                onChange={(e) => setLocations(e.target.value)} 
                placeholder="e.g. 10 mins from Airport, Near Metro Station"
                rows="3"
              />
            </div>

            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ margin: 0 }}>Mark as Premium Property?</label>
              <input type="checkbox" style={{ width: 'auto' }} checked={isPremium} onChange={(e) => setIsPremium(e.target.checked)} />
            </div>

            <div className="form-group">
              <label>Image</label>
              <input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} />
              {imageUrl && !imageFile && <img src={imageUrl} alt="Preview" className="image-preview" />}
            </div>

            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={resetForm}>Cancel</button>
              <button type="submit" className="btn-save" disabled={loading}>
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="admin-list-container">
          {category !== 'premium' && (
            <button className="btn-add" onClick={() => setIsEditing(true)}>+ Add New {category}</button>
          )}
          
          {loading ? (
            <p>Loading...</p>
          ) : properties.length === 0 ? (
            <p>No properties found. Add one!</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Title</th>
                  <th>Location</th>
                  <th>Price</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {properties.map((prop) => (
                  <tr key={prop.id}>
                    <td>
                      {prop.image_url ? (
                        <img src={prop.image_url} alt={prop.title} className="table-img" />
                      ) : (
                        'No Image'
                      )}
                    </td>
                    <td>{prop.title}</td>
                    <td>{prop.location}</td>
                    <td>{prop.price}</td>
                    <td>
                      <button className="btn-edit" onClick={() => handleEdit(prop)}>Edit</button>
                      <button className="btn-delete" onClick={() => handleDelete(prop.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
